import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { contentAPI, profilesAPI } from '../services/api';
import { useToast } from './Toast';
import '../styles/WorkspaceChrome.css';
import './InProgress.css';

/** Statuses shown on this page (approved/published live in Publish). */
const WORKSPACE_STATUSES = [
  'drafting',
  'reviewing',
  'needs_revisions',
];

const EDITABLE_WORKFLOW_STATUSES = WORKSPACE_STATUSES;

function formatStatus(status) {
  if (!status) return '';
  return status.replace(/_/g, ' ');
}

function normalizeDraftStatus(s) {
  if (WORKSPACE_STATUSES.includes(s)) return s;
  if (s === 'approved' || s === 'published') return s;
  if (s === 'draft') return 'drafting';
  if (s === 'review') return 'reviewing';
  return 'drafting';
}

function ResearchBriefs() {
  const { showToast } = useToast();
  const [drafts, setDrafts] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [selectedProfile]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { workflow_kind: 'human_research' };
      if (selectedProfile) params.profile_id = selectedProfile;
      const [draftsRes, profilesRes] = await Promise.all([
        contentAPI.getDrafts(params),
        profilesAPI.getProfiles(),
      ]);
      setDrafts(draftsRes.data);
      setProfiles(profilesRes.data);
    } catch (err) {
      console.error('Error loading research briefs:', err);
      setError(
        `Failed to load draft workspaces: ${err.response?.data?.error || err.message || 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredDrafts = useMemo(() => {
    return drafts.filter((d) => {
      const status = normalizeDraftStatus(d.status);
      if (status === 'approved' || status === 'published') return false;
      if (statusFilter) return status === statusFilter;
      return true;
    });
  }, [drafts, statusFilter]);

  const handleDeleteDraft = async (draftId) => {
    const draft = drafts.find((d) => d.id === draftId);
    if (
      !confirm(
        `Delete draft workspace "${draft?.title}"?\n\nThis cannot be undone.`
      )
    ) {
      return;
    }
    try {
      setError(null);
      await contentAPI.deleteDraft(draftId);
      showToast('Research brief deleted.', 'success');
      loadData();
    } catch (err) {
      console.error('Error deleting draft:', err);
      showToast(
        err.response?.data?.error || err.message || 'Delete failed',
        'error'
      );
    }
  };

  const handleSendToPublishingQueue = async (draftId, title) => {
    if (
      !window.confirm(
        `Send "${title || 'this draft'}" to the publishing queue?\n\nIt will appear under Publish → Publishing queue. A publish checklist (SEO, social, citations) will be generated for CMS handoff.`
      )
    ) {
      return;
    }
    try {
      const res = await contentAPI.sendToPublishingQueue(draftId);
      if (res.data.generation_error) {
        showToast(
          res.data.message ||
            'Sent to publishing queue, but checklist generation failed.',
          'warning',
          8000
        );
      } else {
        showToast('Sent to publishing queue.', 'success', 6000);
      }
      loadData();
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Could not send to publishing queue',
        'error'
      );
    }
  };

  const handleStatusChange = async (draftId, newStatus) => {
    try {
      await contentAPI.updateDraft(draftId, { status: newStatus });
      showToast(`Status: ${formatStatus(newStatus)}`, 'success');
      loadData();
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Update failed',
        'error'
      );
    }
  };

  if (loading) {
    return (
      <div className="in-progress pf-explore research-briefs-page">
        <div className="pf-loading">Loading draft workspaces…</div>
      </div>
    );
  }

  return (
    <div className="in-progress pf-explore research-briefs-page">
      <div className="pf-header">
        <div>
          <h2>Draft workspaces</h2>
          <p className="pf-sub">
            Flow: <strong>Research</strong> → <strong>Write</strong> →{' '}
            <strong>Publish</strong>. When a draft is ready, send it to the publishing queue — it
            leaves this list and appears under <strong>Publish</strong>.
          </p>
        </div>
        <div className="pf-filters">
          <select
            value={selectedProfile}
            onChange={(e) => setSelectedProfile(e.target.value)}
            className="pf-filter-select"
            aria-label="Filter by topic profile"
          >
            <option value="">All topic profiles</option>
            {profiles.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="pf-filter-select"
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            {WORKSPACE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {formatStatus(s)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="pf-error-banner">{error}</div>}

      <div className="research-briefs-root">
        <div className="drafts-list research-briefs-fullwidth pf-list-card">
          <h3>Draft workspaces ({filteredDrafts.length})</h3>
          {filteredDrafts.length === 0 ? (
            <div className="pf-empty">
              <p>No draft workspaces yet.</p>
              <p className="pf-empty-hint">
                From <strong>Write → Draft candidates</strong>, use{' '}
                <strong>Create Draft Workspace</strong> on a queued article, or reopen an existing
                row.
              </p>
            </div>
          ) : (
            <div className="drafts-items">
              {filteredDrafts.map((draft) => {
                const status = normalizeDraftStatus(draft.status);
                return (
                <div key={draft.id} className="draft-item research-brief-row">
                  <div className="draft-item-content">
                    <Link
                      to={`/research-brief/${draft.id}`}
                      className="research-brief-main-link"
                    >
                      <h4>{draft.title}</h4>
                    </Link>
                    <div className="draft-item-meta">
                      {draft.profile_name && (
                        <span className="meta-badge">{draft.profile_name}</span>
                      )}
                      <span className={`meta-badge status-${status}`}>
                        {formatStatus(status)}
                      </span>
                    </div>
                    <div className="draft-item-footer">
                      <span className="draft-word-count">{draft.word_count || 0} words</span>
                      <span className="draft-date">
                        {draft.updated_at
                          ? new Date(draft.updated_at).toLocaleDateString()
                          : draft.created_at
                            ? new Date(draft.created_at).toLocaleDateString()
                            : ''}
                      </span>
                    </div>
                    <div className="research-brief-row-actions">
                      <Link
                        to={`/research-brief/${draft.id}`}
                        className="pf-btn pf-btn-primary"
                      >
                        Open workspace
                      </Link>
                      <button
                        type="button"
                        className="pf-btn pf-btn-send-queue"
                        onClick={() =>
                          void handleSendToPublishingQueue(draft.id, draft.title)
                        }
                      >
                        Send to publishing queue
                      </button>
                      <select
                        aria-label="Brief status"
                        value={status}
                        onChange={(e) =>
                          void handleStatusChange(draft.id, e.target.value)
                        }
                        className="pf-filter-select compact-select"
                      >
                        {EDITABLE_WORKFLOW_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {formatStatus(s)}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => void handleDeleteDraft(draft.id)}
                        className="pf-btn pf-btn-inline-danger"
                        title="Delete brief"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResearchBriefs;
